import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { useUserData } from './UserDataContext';
import * as accountService from '../services/accountService';

// Create the context
const AccountDataContext = createContext(null);

/**
 * Provider component for account data that needs to be refreshed periodically
 * @param {object} props - Component props
 * @returns {JSX.Element} Account data provider
 */
export const AccountDataProvider = ({ children }) => {
  const { user, account: authAccount, unit, isAuthenticated } = useAuth();
  const { userData } = useUserData();
  
  const [accountBalance, setAccountBalance] = useState(null);
  const [usageData, setUsageData] = useState(null);
  const [billing, setBilling] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [failedAttempts, setFailedAttempts] = useState({});
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  /**
   * Check if data needs to be refreshed based on time threshold
   * @param {number} minutes - Minutes threshold for refresh
   * @returns {boolean} True if data should be refreshed
   */
  const shouldRefreshData = useCallback((minutes = 15) => {
    if (!lastUpdated) return true;
    
    const now = new Date();
    const diff = (now - lastUpdated) / (1000 * 60); // Convert to minutes
    return diff > minutes;
  }, [lastUpdated]);

  /**
   * Check if user is a prepaid user based on unit information
   * @returns {boolean} True if user is prepaid
   */
  const isPrepaidUser = useCallback(() => {
    // Check userData.unitInfo first
    if (userData?.unitInfo?.isPrepaid !== undefined) {
      return userData.unitInfo.isPrepaid;
    }
    
    // Then check authContext unit
    if (unit?.isPrepaid !== undefined) {
      return unit.isPrepaid;
    }
    
    // Default to true if we can't determine
    return true;
  }, [userData, unit]);

  /**
   * Reset failed attempts for all or specific endpoint
   * @param {string} endpoint - Optional specific endpoint to reset
   */
  const resetFailedAttempts = useCallback((endpoint = null) => {
    // Reset our internal failedAttempts state
    if (endpoint) {
      setFailedAttempts(prev => {
        const newFailures = {...prev};
        delete newFailures[endpoint];
        return newFailures;
      });
    } else {
      // Reset all failed attempts
      setFailedAttempts({});
    }
    
    // Also reset the accountService's failedEndpoints tracking
    accountService.resetFailedEndpoints(endpoint);
    
    // console.log("Reset all failed endpoints");
  }, []);

  /**
   * Track API call failures to prevent excessive retries
   * @param {string} endpoint - The API endpoint that failed
   * @returns {boolean} - Whether we should try again
   */
  const trackEndpointFailure = useCallback((endpoint) => {
    const now = Date.now();
    const currentFailures = failedAttempts[endpoint] || { count: 0, lastAttempt: 0 };
    const timeSinceLastAttempt = (now - currentFailures.lastAttempt) / 1000; // seconds
    
    // Determine backoff time based on failure count
    let backoffTime = 60; // Default 1 minute
    if (currentFailures.count > 1) backoffTime = 300; // 5 minutes
    if (currentFailures.count > 3) backoffTime = 900; // 15 minutes
    if (currentFailures.count > 5) backoffTime = 3600; // 1 hour
    
    // If we've waited long enough, allow a retry
    if (timeSinceLastAttempt > backoffTime) {
      setFailedAttempts(prev => ({
        ...prev,
        [endpoint]: {
          count: currentFailures.count + 1,
          lastAttempt: now
        }
      }));
      return true;
    }
    
    return false;
  }, [failedAttempts]);

  /**
   * Fetch account balance data
   */
  const fetchAccountBalance = useCallback(async () => {
    if (!isAuthenticated() || !userData?.accountUuid) {
      return;
    }

    // Check if we've had recent failures for this endpoint
    const endpoint = `balance-${userData.accountUuid}`;
    if (failedAttempts[endpoint] && !trackEndpointFailure(endpoint)) {
      // console.log(`Skipping balance fetch due to recent failures`);
      return null;
    }

    try {
      setLoading(true);
      const balanceData = await accountService.getAccountBalance();
      
      // Handle null response (endpoint not responding)
      if (balanceData === null) {
        setFailedAttempts(prev => ({
          ...prev,
          [endpoint]: {
            count: (prev[endpoint]?.count || 0) + 1,
            lastAttempt: Date.now()
          }
        }));
        return null;
      }
      
      // Set account balance data
      setAccountBalance(balanceData);
      
      // Reset failure count on success
      if (failedAttempts[endpoint]) {
        setFailedAttempts(prev => {
          const newFailures = {...prev};
          delete newFailures[endpoint];
          return newFailures;
        });
      }
      
      return balanceData;
    } catch (err) {
      // console.error('Error fetching account balance:', err);
      setError('Failed to load account balance data');
      
      // Track failure
      setFailedAttempts(prev => ({
        ...prev,
        [endpoint]: {
          count: (prev[endpoint]?.count || 0) + 1,
          lastAttempt: Date.now()
        }
      }));
      
      return null;
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, userData, failedAttempts, trackEndpointFailure]);

  /**
   * Fetch usage data
   */
  const fetchUsageData = useCallback(async () => {
    if (!isAuthenticated() || !userData?.accountUuid) {
      return;
    }

    // Check if we've had recent failures for this endpoint
    const endpoint = `usage-${userData.accountUuid}`;
    if (failedAttempts[endpoint] && !trackEndpointFailure(endpoint)) {
      // console.log(`Skipping usage fetch due to recent failures`);
      return null;
    }

    try {
      setLoading(true);
      
      // Since we don't have a real usage endpoint, return mock data
      // console.log('Usage endpoint is not implemented, returning mock data');
      
      // Create mock usage data
      const mockUsageData = {
        status: 'OK',
        readings: [
          { date: new Date().toISOString(), kwh: 0 }
        ],
        summary: {
          daily: { today: 0, yesterday: 0 },
          weekly: { thisWeek: 0, lastWeek: 0 },
          monthly: { currentMonth: 0, lastMonth: 0 }
        }
      };
      
      setUsageData(mockUsageData);
      
      // Reset failure count
      if (failedAttempts[endpoint]) {
        setFailedAttempts(prev => {
          const newFailures = {...prev};
          delete newFailures[endpoint];
          return newFailures;
        });
      }
      
      return mockUsageData;
    } catch (err) {
      // console.error('Error fetching usage data:', err);
      setError('Failed to load usage data');
      
      // Track failure
      setFailedAttempts(prev => ({
        ...prev,
        [endpoint]: {
          count: (prev[endpoint]?.count || 0) + 1,
          lastAttempt: Date.now()
        }
      }));
      
      return null;
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, userData, failedAttempts, trackEndpointFailure]);

  /**
   * Fetch billing data
   */
  const fetchBillingData = useCallback(async () => {
    if (!isAuthenticated() || !userData?.accountUuid) {
      return;
    }

    // Check if we've had recent failures for this endpoint
    const endpoint = `billing-${userData.accountUuid}`;
    if (failedAttempts[endpoint] && !trackEndpointFailure(endpoint)) {
      // console.log(`Skipping billing fetch due to recent failures`);
      return null;
    }

    try {
      setLoading(true);
      
      // Since we don't have a real billing endpoint, return mock data
      // console.log('Billing endpoint is not implemented, returning mock data');
      
      // Create mock billing data
      const mockBillingData = {
        statements: [],
        summary: {
          totalDue: 0,
          lastPayment: {
            date: new Date().toISOString(),
            amount: 0
          }
        }
      };
      
      setBilling(mockBillingData);
      
      // Reset failure count
      if (failedAttempts[endpoint]) {
        setFailedAttempts(prev => {
          const newFailures = {...prev};
          delete newFailures[endpoint];
          return newFailures;
        });
      }
      
      return mockBillingData;
    } catch (err) {
      // console.error('Error fetching billing data:', err);
      setError('Failed to load billing data');
      
      // Track failure
      setFailedAttempts(prev => ({
        ...prev,
        [endpoint]: {
          count: (prev[endpoint]?.count || 0) + 1,
          lastAttempt: Date.now()
        }
      }));
      
      return null;
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, userData, failedAttempts, trackEndpointFailure]);

  /**
   * Refresh all account data
   */
  const refreshAllData = useCallback(async () => {
    if (!isAuthenticated() || !userData?.accountUuid) {
      return;
    }

    // Prevent multiple simultaneous refreshes
    if (isRefreshing) {
      // console.log('Already refreshing data, skipping additional refresh request');
      return;
    }

    setIsRefreshing(true);
    setLoading(true);
    setError(null);

    try {
      // console.log(`Refreshing all data for account: ${userData.accountUuid}`);
      
      const results = await Promise.allSettled([
        fetchAccountBalance(),
        fetchUsageData(),
        fetchBillingData()
      ]);
      
      // Check if any requests were successful
      const anySuccess = results.some(result => result.status === 'fulfilled' && result.value !== null);
      
      if (anySuccess) {
        setLastUpdated(new Date());
        // console.log('Successfully refreshed account data');
      } else {
        // console.log('All data refresh attempts failed');
      }
    } catch (err) {
      // console.error('Error refreshing account data:', err);
      setError('Some account data could not be loaded');
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  }, [isAuthenticated, userData, fetchAccountBalance, fetchUsageData, fetchBillingData, isRefreshing]);

  /**
   * Force data refresh by resetting failed attempts and fetching data
   */
  const forceRefresh = useCallback(async () => {
    // Reset all failed attempts
    resetFailedAttempts();
    
    // Then refresh all data
    return refreshAllData();
  }, [resetFailedAttempts, refreshAllData]);

  /**
   * Refresh data if needed based on time threshold
   * @param {number} minutes - Minutes threshold for refresh
   */
  const refreshIfNeeded = useCallback(async (minutes = 15) => {
    if (shouldRefreshData(minutes)) {
      await refreshAllData();
    }
  }, [shouldRefreshData, refreshAllData]);

  // Refresh data when user or account ID changes, but ONLY then
  useEffect(() => {
    // Prevent duplicate data loading and infinite loops
    let isMounted = true;
    let initialLoadAttempted = false;
    let loadTimeoutId = null;
    
    const loadInitialData = async () => {
      // Skip if already attempted or missing data
      if (initialLoadAttempted || !isAuthenticated() || !userData?.accountUuid || !isMounted) {
        return;
      }
      
      initialLoadAttempted = true;
      
      // Reset failures on first load - but don't do this in the effect to avoid infinite loop
      // console.log('Resetting failure tracking before initial data load');
      // Do the reset directly, not through the function that updates state
      accountService.resetFailedEndpoints();
      setFailedAttempts({});
      
      // Delay the first load to allow components to fully mount
      // Store the timeout ID so we can clean it up if needed
      loadTimeoutId = setTimeout(async () => {
        if (isMounted && !isRefreshing) {
          // console.log('Initial data load from AccountDataContext useEffect');
          try {
            // Just fetch the account balance - that's all we really need
            const balanceData = await accountService.getAccountBalance();
            if (balanceData) {
              setAccountBalance(balanceData);
              setLastUpdated(new Date());
            }
            
            // Wait at least 5 seconds before allowing any subsequent refreshes
            setIsRefreshing(true);
            setTimeout(() => {
              if (isMounted) {
                setIsRefreshing(false);
              }
            }, 5000);
          } catch (error) {
            // console.error('Error loading initial account data:', error);
          }
        }
      }, 1000);
    };
    
    loadInitialData();
    
    return () => {
      isMounted = false;
      if (loadTimeoutId) {
        clearTimeout(loadTimeoutId);
      }
    };
    // Keep the dependency array minimal
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, userData?.accountUuid]);

  return (
    <AccountDataContext.Provider
      value={{
        accountBalance,
        usageData,
        billing,
        loading,
        error,
        lastUpdated,
        fetchAccountBalance,
        fetchUsageData,
        fetchBillingData,
        refreshAllData,
        refreshIfNeeded,
        shouldRefreshData,
        resetFailedAttempts,
        forceRefresh,
        isPrepaidUser
      }}
    >
      {children}
    </AccountDataContext.Provider>
  );
};

/**
 * Hook to use the account data context
 * @returns {object} Account data context
 */
export const useAccountData = () => {
  const context = useContext(AccountDataContext);
  if (!context) {
    throw new Error('useAccountData must be used within an AccountDataProvider');
  }
  return context;
}; 